import type { TFunction } from 'i18next';
import { operatorAllowsFreeTextValue } from './interpretation-rule-field-options';
import type {
  InterpretationRule,
  InterpretationRuleDraft,
  InterpretationRuleField,
  InterpretationRuleFieldOptions,
  RuleAction,
  RuleCondition,
} from './types';

export type RuleAuthoringMode = 'describe' | 'build';

let ruleModalInstanceCounter = 0;

/** Unique key so each modal open remounts with fresh form state. */
export function createRuleModalInstanceKey(): string {
  ruleModalInstanceCounter += 1;
  return `extraction-rule-modal-${ruleModalInstanceCounter}-${Date.now()}`;
}

function newRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function emptyAction(): RuleAction {
  return {
    id: newRowId(),
    field: null,
    value: null,
    valueId: null,
  };
}

export function resolveInitialRuleName(
  initialRule: Partial<InterpretationRule> | undefined,
  _t: TFunction
): string {
  return initialRule?.name?.trim() ?? '';
}

export function createInitialExpandedSteps(
  mode: RuleAuthoringMode,
  options: { expandAllSteps?: boolean; isEdit?: boolean } = {}
): boolean[] {
  const expandAll = Boolean(options.expandAllSteps || options.isEdit);
  if (mode === 'describe') {
    return expandAll ? [true, true] : [true, false];
  }
  return expandAll ? [true, true, true, true] : [true, false, false, false];
}

export function resolveInitialAuthoringMode(
  initialRule?: Partial<InterpretationRule>,
  initialMode?: RuleAuthoringMode
): RuleAuthoringMode {
  if (initialMode) return initialMode;
  if (initialRule?.id) return 'build';

  const hasConditions = (initialRule?.conditions?.length ?? 0) > 0;
  const hasActionValues = (initialRule?.actions ?? []).some(
    (action) => action.value != null && String(action.value).trim() !== ''
  );

  if (hasConditions || hasActionValues) {
    return 'build';
  }

  return 'describe';
}

export function cloneConditionsForForm(
  conditions?: RuleCondition[]
): RuleCondition[] {
  if (!conditions?.length) return [];
  return conditions.map((condition) => ({
    ...condition,
    id: condition.id || newRowId(),
  }));
}

export function cloneActionsForForm(actions?: RuleAction[]): RuleAction[] {
  if (!actions?.length) return [emptyAction()];
  return actions.map((action) => ({
    ...action,
    id: action.id || newRowId(),
  }));
}

/** Strip identity/timestamps for duplicate or cross-entity copy flows. */
export function cloneRuleForCopy(
  rule: InterpretationRule,
  nameSuffix?: string
): Partial<InterpretationRule> {
  const {
    id: _id,
    createdDate: _createdDate,
    updatedDate: _updatedDate,
    applicationConnectionId: _applicationConnectionId,
    ...rest
  } = rule;
  const baseName = rule.name?.trim() ?? '';
  return {
    ...rest,
    name:
      nameSuffix && baseName
        ? `${baseName} (${nameSuffix})`
        : baseName || rest.name,
    conditions: cloneConditionsForForm(rule.conditions),
    actions: cloneActionsForForm(rule.actions),
  };
}

type FieldOption = { label: string; value: string; name?: string };

function normalizeMatchText(value: string): string {
  return value.trim().toLowerCase();
}

function matchFieldOptionValue(
  rawValue: string,
  options: FieldOption[]
): string | undefined {
  const normalized = normalizeMatchText(rawValue);
  if (!normalized) return undefined;

  const exactValue = options.find(
    (option) => normalizeMatchText(String(option.value)) === normalized
  );
  if (exactValue) return exactValue.value;

  const exactLabel = options.find(
    (option) => normalizeMatchText(option.label) === normalized
  );
  if (exactLabel) return exactLabel.value;

  const exactName = options.find(
    (option) =>
      option.name != null && normalizeMatchText(option.name) === normalized
  );
  if (exactName) return exactName.value;

  const partialLabel = options.find((option) => {
    const label = normalizeMatchText(option.label);
    const name = option.name ? normalizeMatchText(option.name) : '';
    return label.includes(normalized) || normalized.includes(label) ||
      (name && (name.includes(normalized) || normalized.includes(name)));
  });
  return partialLabel?.value;
}

/** Raw cell value for rule seeding (IDs vs display fields on transaction rows). */
export function getTransactionFieldRawValue(
  fieldKey: InterpretationRuleField,
  row: Record<string, unknown>
): string | number | null {
  switch (fieldKey) {
    case 'contactName':
      return (row.contactId ?? row.contactName) as string | number | null;
    case 'accountCode':
      return (row.accountId ?? row.accountCode ?? row.accountName) as
        | string
        | number
        | null;
    case 'taxName':
      return (row.taxRateId ?? row.taxName) as string | number | null;
    case 'itemId':
      return (row.itemId ?? row.description) as string | number | null;
    default:
      return row[fieldKey] as string | number | null;
  }
}

/** Map a transaction/list value to rule form value + optional option id. */
export function resolveFieldOptionSelection(
  field: InterpretationRuleField | null | undefined,
  rawValue: string | number | null | undefined,
  fieldOptions: InterpretationRuleFieldOptions
): { value: string | number | null; valueId: string | null } {
  if (field == null || rawValue == null || String(rawValue).trim() === '') {
    return { value: null, valueId: null };
  }

  const options = listFieldOptions(field, fieldOptions);
  const raw = String(rawValue).trim();
  if (!options?.length) {
    return { value: rawValue, valueId: null };
  }

  const byValue = options.find((option) => String(option.value) === raw);
  if (byValue) {
    return { value: byValue.label, valueId: byValue.value };
  }

  const byLabel = options.find((option) => option.label === raw);
  if (byLabel) {
    return { value: byLabel.label, valueId: byLabel.value };
  }

  const byName = options.find(
    (option) => option.name != null && option.name === raw
  );
  if (byName) {
    return { value: byName.label, valueId: byName.value };
  }

  const matchedId = matchFieldOptionValue(raw, options);
  if (matchedId) {
    const option = options.find((item) => item.value === matchedId);
    if (option) {
      return { value: option.label, valueId: option.value };
    }
  }

  return { value: raw, valueId: null };
}

/** Human-readable text for INCLUDES / STARTS_WITH / ENDS_WITH conditions. */
export function resolveConditionTextValue(
  field: InterpretationRuleField | null | undefined,
  rawValue: string | number | null | undefined,
  fieldOptions: InterpretationRuleFieldOptions
): string | null {
  if (rawValue == null || String(rawValue).trim() === '') {
    return null;
  }

  const options = listFieldOptions(field, fieldOptions);
  const raw = String(rawValue).trim();
  if (!options?.length) {
    return raw;
  }

  const selection = resolveFieldOptionSelection(field, rawValue, fieldOptions);
  if (selection.valueId) {
    const option = options.find((item) => item.value === selection.valueId);
    return option?.name ?? String(selection.value ?? raw);
  }

  return raw;
}

export function buildInterpretationRuleDraftFromTransactionField(
  fieldKey: InterpretationRuleField,
  reviewSeed: Record<string, unknown>,
  draftRow: Record<string, unknown>,
  fieldOptions: InterpretationRuleFieldOptions
): Pick<InterpretationRuleDraft, 'conditions' | 'actions'> {
  const seedRaw = getTransactionFieldRawValue(fieldKey, reviewSeed);
  const conditions: RuleCondition[] =
    seedRaw != null && String(seedRaw).trim() !== ''
      ? [
          {
            id: newRowId(),
            field: fieldKey,
            operator: 'INCLUDES',
            value: resolveConditionTextValue(fieldKey, seedRaw, fieldOptions),
            valueId: null,
          },
        ]
      : [];

  const mergedDraft = { ...reviewSeed, ...draftRow };
  const actionRaw = getTransactionFieldRawValue(fieldKey, mergedDraft);
  const actionSelection = resolveFieldOptionSelection(
    fieldKey,
    actionRaw,
    fieldOptions
  );

  return {
    conditions,
    actions: [
      {
        id: newRowId(),
        field: fieldKey,
        value: actionSelection.value,
        valueId: actionSelection.valueId,
      },
    ],
  };
}

function listFieldOptions(
  field: InterpretationRuleField,
  fieldOptions: InterpretationRuleFieldOptions
): FieldOption[] | undefined {
  if (field === 'accountCode') return fieldOptions.accountOptions;
  if (field === 'taxName') return fieldOptions.taxOptions;
  if (field === 'sourceType') return fieldOptions.sourceTypeOptions;
  if (field === 'contactName') return fieldOptions.contactNameOptions;
  if (field === 'country') return fieldOptions.countryOptions;
  if (field === 'itemId') return fieldOptions.itemOptions;
  if (field === 'trackingOption1Id') return fieldOptions.trackingOption1Options;
  if (field === 'trackingOption2Id') return fieldOptions.trackingOption2Options;
  return undefined;
}

/** Map free-text parse output to a dropdown value when options are available. */
export function resolveParsedFieldValue(
  field: InterpretationRuleField | null | undefined,
  rawValue: string | number | null | undefined,
  fieldOptions: InterpretationRuleFieldOptions
): string | number | null {
  if (field == null || rawValue == null) return rawValue ?? null;

  const options = listFieldOptions(field, fieldOptions);
  if (!options?.length) return rawValue;

  const matched = matchFieldOptionValue(String(rawValue), options);
  return matched ?? rawValue;
}

export function applyParsedFieldValuesToConditions(
  conditions: RuleCondition[],
  fieldOptions: InterpretationRuleFieldOptions
): RuleCondition[] {
  return conditions.map((condition) => {
    if (condition.value == null) {
      return condition;
    }

    if (operatorAllowsFreeTextValue(condition.operator)) {
      return {
        ...condition,
        value: resolveConditionTextValue(
          condition.field,
          condition.value,
          fieldOptions
        ),
        valueId: null,
      };
    }

    const resolved = resolveFieldOptionSelection(
      condition.field,
      condition.value,
      fieldOptions
    );
    return {
      ...condition,
      value: resolved.value,
      valueId: resolved.valueId,
    };
  });
}

export function applyParsedFieldValuesToActions(
  actions: RuleAction[],
  fieldOptions: InterpretationRuleFieldOptions
): RuleAction[] {
  return actions.map((action) => {
    if (action.value == null) {
      return action;
    }

    const resolved = resolveFieldOptionSelection(
      action.field,
      action.value,
      fieldOptions
    );
    return {
      ...action,
      value: resolved.value,
      valueId: resolved.valueId,
    };
  });
}

type ScrollIntoContainerAlign = 'start' | 'end' | 'nearest';

/** Scroll a child element into view inside a scrollable modal body. */
export function scrollElementIntoContainer(
  element: HTMLElement | null | undefined,
  container: HTMLElement | null | undefined,
  options?: { align?: ScrollIntoContainerAlign; padding?: number }
): void {
  if (!element || !container) return;

  const padding = options?.padding ?? 16;
  const align = options?.align ?? 'end';

  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  const offsetTop =
    elementRect.top - containerRect.top + container.scrollTop;
  const offsetBottom = offsetTop + elementRect.height;

  const visibleTop = container.scrollTop;
  const visibleBottom = container.scrollTop + container.clientHeight;

  const isFullyVisible =
    offsetTop >= visibleTop + padding &&
    offsetBottom <= visibleBottom - padding;

  if (isFullyVisible) return;

  let targetScrollTop: number;
  if (align === 'end') {
    targetScrollTop = offsetBottom - container.clientHeight + padding;
  } else if (align === 'start') {
    targetScrollTop = offsetTop - padding;
  } else if (offsetTop < visibleTop + padding) {
    targetScrollTop = offsetTop - padding;
  } else {
    targetScrollTop = offsetBottom - container.clientHeight + padding;
  }

  container.scrollTo({
    top: Math.max(0, targetScrollTop),
    behavior: 'smooth',
  });
}
